import React, { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DropdownMenu } from '@radix-ui/themes';
import Avatar from './Avatar';
import { useAuth } from '../../contexts/AuthContext';
import {
  askNotificationPermission,
  requestForToken,
} from '../../utils/firebase';
import { useIsDevice } from '../../hooks/useIsDevice';
import { dropdownStyles } from '../../styles/style';
import toast from 'react-hot-toast';
import Toaster from './Toaster';
import { useFCM } from '../../contexts/FCMContext';
import Button from './Button';
import { routes } from '../../lib/routes';
import { FiChevronLeft } from 'react-icons/fi';
import { LogOut, Menu, Bell, Check } from 'iconoir-react';
import { useBranding, SponsorLine } from '../../contexts/BrandingContext';
import type { ApiResponse } from '../../utils';
import { useRegisterFcmToken } from '../../hooks/useAuthQuery';
import { useDashboardRecentActivity } from '../../hooks/useDashboard';
import type { ActivityItem } from '../../types/dashboard';

interface HeaderProps {
  menuOpen: boolean;
  setMenuOpen: (menuOpen: boolean) => void;
  className?: string;
}

// Format relative time for notifications
const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  return `${diffDays}d ago`;
};

// Format event type to user-friendly text
const formatEventType = (eventType: string): string => {
  const eventMap: Record<string, string> = {
    'AI_SUMMARY_APPROVED': 'Your AI profile summary is ready',
    'NEW_MATCH_FOUND': 'New match found for you',
    'NEW_MATCHES_FOUND': 'New matches found for you',
    'AI_TO_AI_CONVERSATION_INITIATED': 'AI conversation started with a match',
    // Jun-17 (a user round 2, R2-4a): keyed by the stored event value (the
    // backend writes the enum value, not its name) so the interest bell item
    // reads cleanly instead of the lowercased fallback.
    'Interest Received': 'Someone is interested in connecting',
    // Jun-18: connection-request accepted (Matches mutual-approve or Discover
    // mutual-interest). Keyed by the stored enum value.
    'Connection Request Accepted': 'Your connection request was accepted',
  };
  return eventMap[eventType] || eventType.replace(/_/g, ' ').toLowerCase();
};

// Jun-18: route a bell notification to the relevant screen on click instead of
// always dumping the user on the dashboard (made the bell feel "not clickable").
// Keyed by the stored event value, plus the uppercase enum-name variants some
// older write paths still use.
const notificationRoute = (eventType: string): string => {
  switch (eventType) {
    case 'Interest Received':
    case 'Connection Request Accepted':
    case 'New Match Found':
    case 'New Matches Found':
    case 'NEW_MATCH_FOUND':
    case 'NEW_MATCHES_FOUND':
      return routes.matches;
    case 'Initiated User-to-User Conversation':
    case 'USER_TO_USER_CONVERSATION_INITIATED':
      return routes.chat;
    default:
      return routes.home;
  }
};

const Header = ({ menuOpen, setMenuOpen, className }: HeaderProps) => {
  const navigate = useNavigate();
  const { logoDark, name: brandName } = useBranding(); // V1.5.M10 B-P1
  const { user, logout } = useAuth();
  const { handleIncomingNotification } = useFCM();
  const { isMobile, isTablet } = useIsDevice();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const edit = searchParams.get('edit');
  const section = searchParams.get('section');
  const { mutateAsync: registerFcmTokenMutation } = useRegisterFcmToken();
  const [, setShowNotificationPermissionModal] = useState(false);

  // UX-002 FIX: Fetch recent activity for notifications
  const { data: activityData, isLoading: isActivityLoading } = useDashboardRecentActivity();

  // UX-002 FIX: Filter to recent notifications (last 24 hours)
  const notifications = useMemo(() => {
    if (!activityData?.result?.items) return [];
    const items = activityData.result.items as ActivityItem[];
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    return items.filter(item => new Date(item.created_at).getTime() > oneDayAgo).slice(0, 5);
  }, [activityData]);

  const hasNotifications = notifications.length > 0;

  // Jun-16 (a user V1 test, N1): the red dot was driven by "any activity in the
  // last 24h", so it never cleared no matter how many times the bell was
  // opened. Track when the user last opened the bell and only show the dot for
  // activity newer than that.
  const NOTIF_SEEN_KEY = 'notif_last_seen';
  const [lastSeenNotif, setLastSeenNotif] = useState<number>(() => {
    if (typeof window === 'undefined') return 0;
    return Number(localStorage.getItem(NOTIF_SEEN_KEY) || 0);
  });
  const hasUnseenNotif = useMemo(
    () =>
      notifications.some(
        n => new Date(n.created_at).getTime() > lastSeenNotif
      ),
    [notifications, lastSeenNotif]
  );
  const markNotificationsSeen = () => {
    const now = Date.now();
    setLastSeenNotif(now);
    if (typeof window !== 'undefined') {
      localStorage.setItem(NOTIF_SEEN_KEY, String(now));
    }
  };

  useEffect(() => {
    let bc: BroadcastChannel | null = null;

    const initFCM = async () => {
      try {
        const permission = await askNotificationPermission();
        if (permission !== 'granted') {
          setShowNotificationPermissionModal(true);
          return;
        }

        const token = await requestForToken();

        if (!token) {
          return;
        }

        await registerFcmTokenMutation(
          { token: token || '' },
          {
            onSuccess: () => {
              // Create BroadcastChannel for FCM notifications
              bc = new BroadcastChannel('fcm_channel');

              bc.onmessage = event => {
                if (event.data?.type === 'FCM_NOTIFICATION') {
                  const payload = event.data.payload;
                  handleIncomingNotification(payload);
                }
              };
            },
            onError: (error: any) => {
              toast.custom(t => (
                <Toaster
                  message={(error as ApiResponse)?.message as string}
                  type='error'
                  id={t.id}
                />
              ));
            },
          }
        );
      } catch (error) {
        console.error('FCM initialization error:', error);
      }
    };

    initFCM();

    // Cleanup BroadcastChannel on unmount
    return () => {
      if (bc) {
        bc.close();
      }
    };
  }, [handleIncomingNotification, registerFcmTokenMutation]);

  return (
    <div
      className={`h-[5rem] fixed right-0 top-0 flex items-center bg-white border-b border-gray-200 z-10 ${className}`}
    >
      <div className='w-full h-full flex items-center justify-between md:px-8 px-4'>
        <div className='flex items-center gap-4'>
          {(pathname === routes.onboarding && edit && section) ||
          (pathname === routes.onboardingSummary &&
            user?.onboarding_matches) ? (
            <Button
              className='w-6 !p-0 border-body-500 text-body-500'
              size='md'
              variant='outline'
              onClick={() => navigate(-1)}
            >
              <FiChevronLeft className='w-6 h-6' />
            </Button>
          ) : (
            <></>
          )}

          {(isMobile || isTablet) && !pathname.includes(routes.onboarding) && (
            <Button
              className='w-6 !p-0 border-body-500 text-body-500'
              size='md'
              variant='outline'
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <Menu className='w-6 h-6' />
            </Button>
          )}

          {(pathname.includes(routes.onboarding) ||
            pathname.includes(routes.onboardingSummary) ||
            pathname.includes(routes.onboardingAISummary) ||
            isMobile ||
            isTablet) && (
            <div className='flex flex-col'>
              <img
                src={logoDark}
                alt={brandName}
                className='w-[12.5rem] h-auto'
                onClick={() =>
                  user?.onboarding_matches ? navigate(routes.home) : ''
                }
              />
              <SponsorLine className='text-[10px] leading-tight text-gray-500 mt-0.5 select-none' />
            </div>
          )}
        </div>
        <div className='flex items-center gap-3'>
          {/* Notification Bell - UX-002 FIX: Now connected to activity feed */}
          <DropdownMenu.Root
            onOpenChange={open => {
              if (open) markNotificationsSeen();
            }}
          >
            <DropdownMenu.Trigger>
              <button
                className='relative p-2.5 rounded-lg hover:bg-gray-100 transition-colors'
                type='button'
                aria-label='Notifications'
              >
                <Bell className='w-5 h-5 text-body-500' />
                {/* Jun-16 (a user V1 test, N1): only show the dot for UNSEEN
                    activity (newer than the last time the bell was opened). */}
                {hasUnseenNotif && (
                  <span className='absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full' />
                )}
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className={`${dropdownStyles.content} min-w-[18rem] max-w-[22rem]`}>
              <div className='px-4 py-3 border-b border-gray-100'>
                <h3 className='text-sm font-semibold text-heading-500'>Notifications</h3>
              </div>
              {isActivityLoading ? (
                <div className='py-6 px-4 text-center'>
                  <p className='text-sm text-body-400'>Loading...</p>
                </div>
              ) : hasNotifications ? (
                <div className='max-h-[20rem] overflow-y-auto'>
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className='px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer'
                      onClick={() => navigate(notificationRoute(notification.event_type))}
                    >
                      <p className='text-sm text-body-500 mb-1'>
                        {formatEventType(notification.event_type)}
                      </p>
                      <p className='text-xs text-gray-400'>
                        {getRelativeTime(notification.created_at)}
                      </p>
                    </div>
                  ))}
                  <div
                    className='px-4 py-2 text-center cursor-pointer hover:bg-gray-50'
                    onClick={() => navigate(routes.home)}
                  >
                    <span className='text-xs text-primary-500 font-medium'>View all activity</span>
                  </div>
                </div>
              ) : (
                <div className='py-8 px-4 text-center'>
                  <div className='w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3'>
                    <Check className='w-6 h-6 text-gray-400' />
                  </div>
                  <p className='text-sm text-body-400 mb-1'>You're all caught up!</p>
                  <p className='text-xs text-gray-400'>No new notifications</p>
                </div>
              )}
            </DropdownMenu.Content>
          </DropdownMenu.Root>

          {/* User Menu */}
          <DropdownMenu.Root>
            <DropdownMenu.Trigger>
              <button className={dropdownStyles.trigger} type='button'>
                <Avatar
                  shape='square'
                  size='lg'
                  name={user?.first_name + ' ' + user?.last_name}
                  src={user?.avatar}
                />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content className={dropdownStyles.content}>
              <DropdownMenu.Item
                className={`${dropdownStyles.item} hover:!bg-transparent !h-auto [.rt-BaseMenuItem]:!cursor-default`}
              >
                <div className='flex flex-col items-center justify-center w-full py-4 px-3 min-w-[14rem]'>
                  <div className='text-center mb-6'>
                    <p className='text-lg text-primary-500 font-heading transform-capitalize font-medium mb-0'>
                      {user?.first_name + ' ' + user?.last_name}
                    </p>
                    <p className='text-xs font-semibold mb-0'>{user?.email}</p>
                  </div>
                  <Button
                    size='sm'
                    variant='outline'
                    className='text-center w-full'
                    onClick={logout}
                  >
                    <LogOut className='w-6 h-6' /> Logout
                  </Button>
                </div>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </div>
      </div>
    </div>
  );
};

export default Header;
